import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@store/utils';

interface StripeUser {
  email: string;
  first_name: string;
  last_name: string;
  address: string;
  zip_code: string;
  state: string;
}
export interface SetupProducer {
  npn: number;
}
export interface PreparePolicyholderProfile {
  email: string;
  first_name: string;
  last_name: string;
  business: string;
  title: string;
  ein?: string;
}
export interface PrepareSubscriptionInfo {
  token: string;
}
export interface PayinInfo {
  token: string;
  mailing_address: string;
  mailing_city: string;
  mailing_state: string;
  mailing_zip: string;
  phone_number: string;
  ssn_last4: string;
  date_of_birth: string;
}
interface SelectSubscriptionInfo {
  token: string;
  yearly_subscription: boolean;
  redirect_success_url: string;
  redirect_cancel_url: string;
  effective_date: string;
  expiration_date: string;
}
export enum AnnualRevenue {
  '<$500,000' = 1,
  '$500K to $1M' = 2,
  '$1M to $2M' = 3,
  '$2M to $4M' = 4,
  '$4M to $8M' = 5,
  '$8M to $16M' = 6,
  '$16M to $32M' = 7,
  '$32M to $50M' = 8,
}
export enum EmployeesNumber {
  '1 to 9' = 1,
  '10 to 24' = 2,
  '25 to 49' = 3,
  '50 to 99' = 4,
  '100 to 149' = 5,
  '150+' = 6,
}
interface PropertyDetails {
  address: string;
  zip: string;
  annual_revenue: AnnualRevenue | null;
  employees_number: EmployeesNumber | null;
}
export interface CoveredRisk {
  risk: number;
  pricing_plan?: number | null;
  property: PropertyDetails;
}
interface Risk {
  token: string;
  type_of_business?: string;
  state?: string;
  covered_risks: CoveredRisk[];
  affiliate_id?: string;
}
export interface DataState {
  data: any;
  status: 'idle' | 'loading' | 'failed' | 'succeeded';
  error: string | null;
}
export interface CancelPolicy {
  id: number;
  cancel_reason?: string | {};
}
export const coverageApi = createApi({
  reducerPath: 'coverage',
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getCategories: builder.query<any, any>({
      query: () => ({
        url: `categories/`,
      }),
    }),
    getPricingPlan: builder.query<any, any>({
      query: (query: string) => ({
        url: `pricing_plan/${query}`,
      }),
    }),
    getUtilityByCounty: builder.query<any, any>({
      query: (countyFIPS: number) => ({
        url: `utilities/?counties=${countyFIPS}`,
      }),
    }),
    setupProducer: builder.mutation<any, SetupProducer>({
      query: (payload) => ({
        url: `producer_profile/`,
        method: 'PUT',
        body: {
          ...payload,
        },
      }),
    }),
    updateProducer: builder.mutation<any, SetupProducer>({
      query: (payload) => ({
        url: `producer_profile/`,
        method: 'PATCH',
        body: {
          ...payload,
        },
      }),
    }),
    getProducerInfo: builder.query<any, any>({
      query: () => ({
        url: `producer_profile/`,
      }),
    }),
    getProducerAffiliates: builder.query<any, any>({
      query: () => ({
        url: `producer_profile/affiliates/`,
      }),
    }),
    prepareUser: builder.mutation<any, StripeUser>({
      query: (payload) => ({
        url: `prepare_user/`,
        method: 'POST',
        body: {
          ...payload,
        },
      }),
    }),
    preparePolicyholderProfile: builder.mutation<
      any,
      PreparePolicyholderProfile
    >({
      query: (payload) => ({
        url: `prepare_policyholder/`,
        method: 'POST',
        body: {
          ...payload,
        },
      }),
    }),
    preparePolicy: builder.mutation<any, Risk>({
      query: (payload) => ({
        url: `prepare_policy/`,
        method: 'POST',
        body: {
          ...payload,
        },
      }),
    }),
    prepareSubscription: builder.mutation<any, PrepareSubscriptionInfo>({
      query: (payload) => ({
        url: `prepare_subscription/`,
        method: 'POST',
        body: {
          ...payload,
        },
      }),
    }),
    preparePayIn: builder.mutation<any, PayinInfo>({
      query: (payload) => ({
        url: `prepare_payin/`,
        method: 'POST',
        body: {
          ...payload,
        },
      }),
    }),
    selectSubscription: builder.mutation<any, SelectSubscriptionInfo>({
      query: (payload) => ({
        url: `select_subscription/`,
        method: 'POST',
        body: {
          ...payload,
        },
      }),
    }),
    confirmPolicy: builder.mutation<any, PrepareSubscriptionInfo>({
      query: (payload) => ({
        url: `confirm_policy/`,
        method: 'POST',
        body: {
          ...payload,
        },
      }),
    }),
    getPolicy: builder.query<any, any>({
      query: (query: string) => ({
        url: `policy/`,
      }),
    }),
    cancelPolicy: builder.mutation<any, CancelPolicy>({
      query: (payload) => ({
        url: `policy/${payload.id}`,
        method: 'POST',
        body: {
          cancel_reason: payload.cancel_reason,
        },
      }),
    }),
    getPolicyTerms: builder.query<any, any>({
      query: (query: string) => ({
        url: `policy_terms/`,
      }),
    }),
    getAffiliateInfo: builder.query<any, any>({
      query: () => ({
        url: `affiliate_profile/`,
      }),
    }),
    updateAffiliate: builder.mutation<any, any>({
      query: (payload) => ({
        url: `affiliate_profile/`,
        method: 'PATCH',
        body: {
          ...payload,
        },
      }),
    }),
    getAffiliateProducers: builder.query<any, any>({
      query: () => ({
        url: `affiliate_profile/producers/`,
      }),
    }),
    getAffiliatePlans: builder.query<any, any>({
      query: () => ({
        url: `affiliate_profile/plans/`,
      }),
    }),
    getProducerPlans: builder.query<any, string>({
      query: (npn) => ({
        url: `affiliate_profile/producers/${npn}/plans/`,
      }),
    }),
    getPlanTerms: builder.query<any, string>({
      query: (planNumber) => ({
        url: `plans/${planNumber}/terms/`,
      }),
    }),
    getAllAffiliates: builder.query<any, { search?: string }>({
      query: (params) => ({
        url: `affiliates/`,
        params,
      }),
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useLazyGetPricingPlanQuery,
  useLazyGetUtilityByCountyQuery,
  usePrepareUserMutation,
  usePreparePolicyholderProfileMutation,
  useSetupProducerMutation,
  useUpdateProducerMutation,
  useLazyGetProducerInfoQuery,
  usePrepareSubscriptionMutation,
  useConfirmPolicyMutation,
  usePreparePayInMutation,
  useSelectSubscriptionMutation,
  usePreparePolicyMutation,
  useGetPolicyTermsQuery,
  useGetPolicyQuery,
  useLazyGetPolicyQuery,
  useCancelPolicyMutation,
  useLazyGetAffiliateInfoQuery,
  useUpdateAffiliateMutation,
  useLazyGetAffiliateProducersQuery,
  useLazyGetAffiliatePlansQuery,
  useGetAffiliateProducersQuery,
  useLazyGetProducerPlansQuery,
  useLazyGetPlanTermsQuery,
  useLazyGetAllAffiliatesQuery,
  useLazyGetProducerAffiliatesQuery,
  useGetProducerAffiliatesQuery,
} = coverageApi;
